import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Button } from '@mui/material'
import { AgGridReact } from 'ag-grid-react'
import ShowLastUpdateDatetime from 'components/commonparts/ShowLastUpdateDatetime/ShowLastUpdateDatetime'
import { LoadingOverlay } from 'components/commonparts/index'
import { useGridBase } from 'hooks/useGridBase'

import type { ColDef } from 'ag-grid-community'
import type { Item, MstItem } from 'api-client'
import type { DataListStatus } from 'types/types'

import 'utils/mst.scss'

interface DataListProps {
  list: Item
  isLoading: boolean
  listStatus: DataListStatus  
  onListStatusChange: (status: DataListStatus) => void
  onRowSelect: (item: MstItem) => void
  gridRef?: React.RefObject<AgGridReact>
  findSearchValue?: string
  onFindChanged?: (event: any) => void
  isSearchVisible?: boolean
}

function DataList({ 
  list, 
  isLoading, 
  listStatus, 
  onListStatusChange, 
  onRowSelect,
  gridRef: externalGridRef,
  findSearchValue: externalFindSearchValue,
  onFindChanged: externalOnFindChanged,
  isSearchVisible = false,
}: DataListProps) {
  const internalGridRef = useRef<AgGridReact<MstItem>>(null)
  const gridRef = externalGridRef || internalGridRef
  const [loadingState, setLoadingState] = useState<boolean>(false)
  const lastApiCallRef = useRef<number>(0)
  
  const {
    dateFormatter,
    numberValueFormatter,
    onGridReady,
    localeText,
    defaultColDef: baseDefaultColDef,
    autoSizeStrategy,
    dateTreeSetFilterParams,
    parseDateValue,
  } = useGridBase({
    gridRef,
    isLoading: isLoading || loadingState,
  })

  const defaultColDef = useMemo(() => ({
    ...baseDefaultColDef,
    resizable: true,
    sortable: true,
    filter: true,
  }), [baseDefaultColDef])

  // Custom cell renderer for selection button
  const SelectionCellRenderer = useCallback((params: any) => {
    // Ensure we have valid data before rendering
    if (!params || !params.data) {
      return null
    }

    const handleSelect = () => {
      onRowSelect(params.data)
    }

    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100%',
        width: '100%',
        padding: '2px'
      }}>
        <Button
          variant="contained"
          size="small"
          onClick={handleSelect}
          sx={{ 
            minWidth: 60, 
            height: 28,
            fontSize: '12px'
          }}
        >
          選択
        </Button>
      </div>
    )
  }, [onRowSelect])

  const columnDefs = useMemo<ColDef[]>(() => [
    {
      headerName: '選択',
      field: 'choiceBtn',
      sortable: false,
      filter: false,
      resizable: false,
      width: 80,
      minWidth: 80,
      maxWidth: 80,
      pinned: 'left',
      cellRenderer: SelectionCellRenderer,
      suppressSizeToFit: true,
      lockPosition: true,
    },
    {
      headerName: '商品コード',
      field: 'itemCode',
      // minWidth: 150,
      cellClass: 'string-cell',
    },
    {
      headerName: '社内商品コード',
      field: 'orgItemCode',
      // minWidth: 150,
      cellClass: 'string-cell',
    },
    {
      headerName: '商品名称',
      field: 'itemName',
      // minWidth: 250,
      cellClass: 'string-cell',
    },
    {
      headerName: 'メーカー名',
      field: 'makerName',
      // minWidth: 150,
      cellClass: 'string-cell',
    },
    {
      headerName: '仕入先ID',
      field: 'orgSupplierId',
      // minWidth: 150,
      cellClass: 'string-cell',
    },
    {
      headerName: 'PSI仕入先ID',
      field: 'supplierCompanyId',
      // minWidth: 150,
      cellClass: 'string-cell',
    },
    {
      headerName: '仕入先名',
      field: 'supplierName',
      // minWidth: 150,
      cellClass: 'string-cell',
    },
    {
      headerName: '発注単位',
      field: 'orderUnitTypeName',
      // minWidth: 150,
      cellClass: 'string-cell',
    },
    {
      headerName: 'ケースロット数',
      field: 'caseLot',
      // minWidth: 150,
      cellClass: 'number-cell',
      filter: 'agNumberColumnFilter',
    },
    {
      headerName: '最低発注量',
      field: 'minOrderQuantity',
      // minWidth: 150,
      cellClass: 'number-cell',
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => {
        if (!params || params.value == null) return '';
        return Number(params.value).toLocaleString();
      },
    },
    {
      headerName: '発売日',
      field: 'salesStartdate',
      filter: 'agMultiColumnFilter',
      filterParams: {
        filters: [
          {filter: 'agDateColumnFilter'},
          {filter: 'agSetColumnFilter',
           filterParams: dateTreeSetFilterParams,
          },
        ],
        closeOnApply: true,
      },
      // minWidth: 90,
      valueFormatter: dateFormatter,
      valueParser: parseDateValue,
    },
    {
      headerName: '終了日',
      field: 'salesEnddate',
      cellClass: 'date-cell',
      filter: 'agMultiColumnFilter',
      filterParams: {
        filters: [
          {filter: 'agDateColumnFilter'},
          {filter: 'agSetColumnFilter',
           filterParams: dateTreeSetFilterParams,
          },
        ],
        closeOnApply: true,
      },
      // minWidth: 90,
      valueFormatter: dateFormatter,
      valueParser: parseDateValue,
    },
    {
      headerName: '規格',
      field: 'spec',
      // minWidth: 150,
      cellClass: 'string-cell',
    },
    {
      headerName: 'ケースサイズ 長手（mm）',
      field: 'longside',
      // minWidth: 220,
      cellClass: 'number-cell',
      filter: 'agNumberColumnFilter',
    },
    {
      headerName: 'ケースサイズ 短手（mm）',
      field: 'shortside',
      // minWidth: 220,
      cellClass: 'number-cell',
      filter: 'agNumberColumnFilter',
    },
    {
      headerName: 'ケースサイズ 高さ（mm）',
      field: 'height',
      // minWidth: 220,
      cellClass: 'number-cell',
      filter: 'agNumberColumnFilter',
    },
    {
      headerName: 'ケースサイズ 重量（g）',
      field: 'weight',
      // minWidth: 200,
      cellClass: 'number-cell',
      filter: 'agNumberColumnFilter',
    },
    {
      headerName: 'パレット最大積付数',
      field: 'maxPalletQuantity',
      // minWidth: 170,
      cellClass: 'number-cell',
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => {
        if (!params || params.value == null) return '';
        return Number(params.value).toLocaleString();
      },
    },
    {
      headerName: 'パレット積載数（面）',
      field: 'palletQuantityFace',
      // minWidth: 200,
      cellClass: 'number-cell',
      filter: 'agNumberColumnFilter',
    },
    {
      headerName: 'パレット積載数（段）',
      field: 'palletQuantityTier',
      // minWidth: 200,
      cellClass: 'number-cell',
      filter: 'agNumberColumnFilter',
    },
    {
      headerName: 'JANコード（バラ）',
      field: 'jan',
      // minWidth: 170,
      cellClass: 'string-cell',
    },
    {
      headerName: 'JANコード（ケース）',
      field: 'janCase',
      // minWidth: 180,
      cellClass: 'string-cell',
    },
    {
      headerName: 'GTINコード（バラ）',
      field: 'gtin13',
      // minWidth: 170,
      cellClass: 'string-cell',
    },
    {
      headerName: 'GTINコード（ケース）',
      field: 'gtin14',
      // minWidth: 180,
      cellClass: 'string-cell',
    },
    {
      headerName: 'ITFコード',
        field: 'itf',
        // minWidth: 150,
      cellClass: 'string-cell',
    },
    {
      headerName: 'SDPコード',
      field: 'sdp',
      // minWidth: 150,
      cellClass: 'string-cell',
    },
    {
      headerName: '後継商品コード',
      field: 'replaceItemCode',
      // minWidth: 150,
      cellClass: 'string-cell',
    },
    {
      headerName: 'メーカーGS1事業者コード',
      field: 'makerGln',
      // minWidth: 220,
      cellClass: 'string-cell',
    },
  ], [SelectionCellRenderer, dateFormatter])

  // Auto-size columns when data changes (excluding selection column)
  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api || api.isDestroyed()) return;
    
    if (list.list.length > 0) {
      // Add a small delay to let the grid render data first
      setTimeout(() => {
        if (!api.isDestroyed()) {
          // Auto-size all columns except the selection column
          const allColumns = api.getColumns()
          const columnsToSize = allColumns?.filter(col => col.getColId() !== 'choiceBtn') || []
          if (columnsToSize.length > 0) {
            api.autoSizeColumns(columnsToSize.map(col => col.getColId()))
          }
        }
      }, 100)
    }
  }, [list.list])

  // Container style for dialog height - adjust for SearchBox visibility
  const containerStyle = useMemo(() => {
    const searchBoxHeight = isSearchVisible ? 60 : 0
    return { 
      width: '100%', 
      height: `calc(100vh - ${300 + searchBoxHeight}px)`,
      display: 'flex',
      flexDirection: 'column' as const
    }
  }, [isSearchVisible])

  const gridStyle = useMemo(() => {
    const searchBoxHeight = isSearchVisible ? 60 : 0
    return { 
      width: '100%', 
      height: `calc(100vh - ${340 + searchBoxHeight}px)` 
    }
  }, [isSearchVisible])

  // Handle double click for row selection
  const onRowDoubleClicked = useCallback((event: any) => {
    onRowSelect(event.data)
  }, [onRowSelect])

  // Handle pagination changes (page size, page number)
  const onPaginationChanged = useCallback(() => {
    const now = Date.now()
    if (!gridRef.current?.api) {
      return
    }
    
    const currentPage = gridRef.current.api.paginationGetCurrentPage()
    const pageSize = gridRef.current.api.paginationGetPageSize()

    // Don't trigger API call during loading to avoid double calls
    if (isLoading || loadingState) {
      return
    }

    // Debounce: only allow one API call per 500ms
    if (now - lastApiCallRef.current < 500) {
      return
    }

    if (currentPage !== listStatus.paginationModel.page || pageSize !== listStatus.paginationModel.pageSize) {
      // Update debounce timestamp
      lastApiCallRef.current = now

      // Set loading state when making API call
      setLoadingState(true)

      // When page size changes, reset to page 0
      const targetPage = pageSize !== listStatus.paginationModel.pageSize ? 0 : currentPage

      onListStatusChange({
        sortModel: listStatus.sortModel,
        paginationModel: { page: targetPage, pageSize }
      })
    }
  }, [listStatus.sortModel, listStatus.paginationModel.page, listStatus.paginationModel.pageSize, onListStatusChange, isLoading, loadingState])

  // Reset loading state when new data arrives
  useEffect(() => {
    if (!isLoading && loadingState) {
      setLoadingState(false)
    }
  }, [isLoading, loadingState])

  // Hide pagination panel during loading to prevent inaccurate information
  useEffect(() => {
    const timer = setTimeout(() => {
      const paginationPanel = document.querySelector('.ag-paging-panel')
      if (paginationPanel) {
        (paginationPanel as HTMLElement).style.visibility = (isLoading || loadingState) ? 'hidden' : 'visible'
      }
    }, 10)
    return () => clearTimeout(timer)
  }, [isLoading, loadingState])

  // Loading and data management effects
  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api || api.isDestroyed()) return;

    if (isLoading || loadingState) {
      api.setGridOption('loading', true);
    } else if (list.list.length === 0) {
      api.showNoRowsOverlay();
    } else {
      api.setGridOption('loading', false);
      api.hideOverlay();
      // Force refresh to ensure headers are properly displayed
      setTimeout(() => {
        if (!api.isDestroyed()) {
          api.refreshHeader();
        }
      }, 50);
    }
  }, [isLoading, loadingState, list.list])

  return (
    <div style={containerStyle}>
      <ShowLastUpdateDatetime<MstItem> list={list.list} lastUpdateDatetime={list.lastUpdateDatetime} />
      <div style={{ flexGrow: 1, boxSizing: 'border-box' }}>
        <div style={gridStyle} className="ag-theme-balham">
          <AgGridReact
            ref={gridRef}
            rowData={list.list}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            autoSizeStrategy={autoSizeStrategy}
            onGridReady={onGridReady}
            localeText={localeText}
            loadingOverlayComponent={LoadingOverlay}
            getRowId={(params) => params.data.id}
            onRowDoubleClicked={onRowDoubleClicked}
            rowHeight={36}
            pagination
            paginationPageSize={listStatus.paginationModel.pageSize}
            paginationPageSizeSelector={[100, 300, 500]}
            onPaginationChanged={onPaginationChanged}
            suppressPaginationPanel={false}
            suppressScrollOnNewData
            suppressColumnVirtualisation
            rowModelType="clientSide"
            findSearchValue={externalFindSearchValue}
            onFindChanged={externalOnFindChanged}
          />
        </div>
      </div>
    </div>
  )
}

export default DataList
